// Centralized EN/FR translations for the whole site.
// Keep keys stable — pages read via t.<section>.<key>.

export const translations = {
  en: {
    nav: {
      home: "Home",
      book: "Book",
      resources: "Resources",
      tools: "Tools",
      about: "About",
      contact: "Contact",
    },
    cta: {
      buy: "Buy the book",
      buyNow: "Buy now",
      getChapters: "Get the first 3 chapters",
      downloadChapters: "Download the first 3 chapters",
      alsoOn: "Also on",
      readMoreAmazon: "Read more reviews on Amazon",
      learnMore: "Learn more",
      exploreResources: "Explore resources",
    },
    hero: {
      eyebrow: "By Antoine B. Carrière",
      title1: "Most entrepreneurs know their",
      title_offer: "offer",
      title2: "better than they know their",
      title_clients: "clients",
      subtitle:
        "This book helps you better understand the people behind the buying decision — so you can build, communicate, and grow with greater clarity.",
    },
    home: {
      problem: {
        title1: "Do you really know who your",
        title_blue: "clients",
        title2: "are?",
        p1a: "Many entrepreneurs can explain their product or service with great precision.",
        p1b: "Few can describe — with the same clarity — the person who actually decides to buy.",
        p2a: "We have access to more information than ever.",
        p2b: "But having access to data is not the same as understanding people.",
        p3: "And without that understanding, too many decisions rest on assumptions.",
      },
      why: {
        title1: "A business cannot exist without",
        title_blue: "clients",
        p1: "A business moves forward when people recognize the value of what it offers.",
        p2a: "Not simply because the idea is good.",
        p2b: "Not only because the product is ready.",
        p2c: "But because a real person recognizes a need and decides to act.",
        p3a: "Understanding that person is not secondary.",
        p3b: "It is at the core of everything else.",
        imgCaption: "Real people. Real decisions. Your real business.",
      },
      explore: {
        title1: "A different",
        title_blue: "starting point",
        subtitle:
          "This book invites you to step back and revisit how you define your clients. You will explore:",
        items: [
          "why so many entrepreneurs struggle to clearly define their clients",
          "what is often missing in the way client understanding is approached",
          "how to better recognize the factors that shape a real buying decision",
          "how to bring this thinking into your business in a more intentional way",
        ],
      },
      sample: {
        title1: "Start with the first",
        title_blue: "3 chapters",
        body:
          "If you do not yet have a clear idea of who your clients really are, this is a good place to begin. Download the first chapters and start a more concrete reflection on how you define and understand the people you want to reach.",
      },
      testimonialsTitle: "What readers are",
      testimonialsTitleBlue: "saying",
      aboutShort: {
        badge: "The author",
        title: "About the author",
        p1a: "Antoine B. Carrière",
        p1b:
          " is an entrepreneur, coach, and facilitator. For more than two decades, he has been helping people who want to move forward with greater clarity in their decisions, their work, and their projects.",
        p2:
          "His work focuses on a better understanding of oneself, of others, and of the choices needed to build a more aligned career or business.",
      },
      resourcesPreview: {
        title1: "Go further with practical",
        title_blue: "resources",
        subtitle:
          "The book is supported by tools, articles, and reflection material designed to help you put the ideas into practice.",
        cards: {
          tools: {
            title: "Tools",
            body: "Practical frameworks you can use to define and understand your clients.",
            cta: "Access tools",
          },
          articles: {
            title: "Articles",
            body: "Short reflections you can read in a single sitting.",
            cta: "Read articles",
          },
          quotes: {
            title: "Quotes",
            body: "Lines from the book you can carry into your week.",
            cta: "View quotes",
          },
        },
      },
    },
    book: {
      eyebrow: "The book",
      title1: "The",
      title_blue: "book",
      subtitle:
        "A concrete starting point for entrepreneurs who want to better understand their clients.",
      pages: "Pages",
      format: "Format",
      language: "Language",
      coreIdea: {
        title1: "A business cannot exist without",
        title_blue: "clients",
        p1: "Your business moves forward when people see value in what you offer.",
        p2a: "Not when your idea is good.",
        p2b: "Not when your product is complete.",
        p2c: "But when a real person recognizes a need and decides to act.",
        p3a: "Understanding that person is not optional.",
        p3b: "It is central to everything that follows.",
      },
      gap: {
        title1: "The majority of entrepreneurs do not know their",
        title_blue: "clients",
        p1:
          "Entrepreneurs often become very skilled at what they offer. But that expertise does not automatically extend to understanding the people they are trying to serve.",
        p2: "This book looks directly at that gap.",
      },
      gains: {
        title1: "What you will",
        title_blue: "gain",
        items: [
          "a clearer definition of the client",
          "a better understanding of what influences a buying decision",
          "a stronger alignment between your offer and the people you want to reach",
          "more intentional and more relevant communication",
        ],
      },
      sample: {
        title1: "Read before you",
        title_blue: "commit",
        body:
          "Download the first 3 chapters to see if this approach matches the way you think about your business and your clients.",
      },
      testimonialsTitle: "Readers",
      formats: {
        title: "Formats",
        printAvailable: "Print available",
        otherComing: "Other formats coming",
      },
      ctaTitle: "Read before you buy.",
      ctaSubtitle:
        "The first 3 chapters will tell you if this book belongs on your shelf.",
    },
    resources: {
      title: "Resources",
      subtitle:
        "Tools and content to help you better define and understand your clients.",
      intro:
        "Everything here is an extension of the book. Choose what is useful to you right now, and leave the rest for later.",
      tools: {
        title: "Tools",
        body: "Practical frameworks to define and understand your clients. Built to be used, not just read.",
        cta: "Access tools",
      },
      articles: {
        title: "Articles",
        body: "Short reflections on the ideas that shape the book — written to be useful in a single sitting.",
        cta: "Read articles",
        comingSoon: "Coming soon",
      },
      quotes: {
        title: "Quotes",
        body: "Lines from the book to carry into your week, share with a co-founder, or sit with on your own.",
        cta: "View quotes",
      },
      ctaTitle: "The best resource is still the book.",
      ctaSubtitle: "Everything here is built to be used alongside it.",
    },
    tools: {
      title: "Tools",
      subtitle: "Practical resources to help you better define your ideal client.",
      note:
        "Each tool is designed to be used alongside the book. Full, downloadable versions are being prepared.",
      ctaTitle: "Tools work better with the book.",
      ctaSubtitle: "Start by reading the first 3 chapters.",
    },
    about: {
      title: "About the author",
      p1:
        "Antoine B. Carrière is an entrepreneur, coach, and facilitator. For more than two decades, he has been helping people who want to move forward with greater clarity in their decisions, their work, and their projects.",
      p2:
        "His work focuses on a better understanding of oneself, of others, and of the choices needed to build a more aligned career or business.",
      ctaTitle: "One question. A whole book.",
      ctaSubtitle:
        "If this sounds like something you've been needing, start with the preview.",
    },
    contact: {
      title: "Contact",
      subtitle:
        "A short note is enough. Say hello, ask a question, or share what the book brought up for you.",
      local: "Local",
      toll: "Toll-free",
      email: "Email",
      name: "Your name",
      emailLabel: "Email",
      message: "Message",
      placeholderName: "Jane Doe",
      placeholderMsg: "What's on your mind?",
      send: "Send message",
      sending: "Sending…",
      success: "Thank you for writing.",
      successBody: "Your message was received.",
      another: "Send another",
      validation: "Please fill in all fields.",
      errorToast: "Something went wrong. Please try again.",
    },
    ctaSection: {
      defaultTitle: "Start with the first 3 chapters.",
      defaultSubtitle: "If the book is for you, you'll know by page 30.",
    },
    footer: {
      explore: "Explore",
      startHere: "Start here",
      startHereBody:
        "The fastest way to find out if this book is for you is to read the first three chapters.",
      copyright: "All rights reserved.",
    },
    testimonials: {
      title: "What readers are saying",
      titleBlue: "saying",
      readMore: "Read more reviews on Amazon →",
    },
    login: {
      title: "Admin login",
      subtitle: "Restricted area. Sign in to manage the site.",
      passwordLabel: "Password",
      submit: "Sign in",
      error: "Incorrect password.",
      success: "Welcome back.",
    },
    admin: {
      title: "Admin dashboard",
      welcome: "Manage your leads, messages, and content.",
      stats: {
        leads: "Chapter downloads",
        contacts: "Contact messages",
      },
      tabs: {
        leads: "Downloads",
        contacts: "Messages",
        content: "Content",
      },
      exportCsv: "Export as CSV",
      noLeads: "No downloads yet.",
      noContacts: "No messages yet.",
      logout: "Sign out",
      comingSoon: "Coming soon",
      contentCards: {
        posts: {
          title: "Posts & articles",
          body: "Write, publish, and manage blog posts that support the book.",
        },
        podcasts: {
          title: "Podcasts",
          body: "Upload episodes, add show notes, and connect your feed.",
        },
        services: {
          title: "Services",
          body: "Publish workshops, client development programs, and coaching offers.",
        },
      },
    },
  },
  fr: {
    nav: {
      home: "Accueil",
      book: "Le livre",
      resources: "Ressources",
      tools: "Outils",
      about: "À propos",
      contact: "Contact",
    },
    cta: {
      buy: "Acheter le livre",
      buyNow: "Acheter maintenant",
      getChapters: "Obtenir les 3 premiers chapitres",
      downloadChapters: "Télécharger les 3 premiers chapitres",
      alsoOn: "Aussi sur",
      readMoreAmazon: "Voir plus d'avis sur Amazon",
      learnMore: "En savoir plus",
      exploreResources: "Explorer les ressources",
    },
    hero: {
      eyebrow: "Par Antoine B. Carrière",
      title1: "La plupart des entrepreneurs connaissent mieux leur",
      title_offer: "offre",
      title2: "que leurs",
      title_clients: "clients",
      subtitle:
        "Ce livre vous aide à mieux comprendre les personnes derrière la décision d'achat afin de bâtir, communiquer et croître avec plus de clarté.",
    },
    home: {
      problem: {
        title1: "Connaissez-vous vraiment vos",
        title_blue: "clients",
        title2: "?",
        p1a: "De nombreux entrepreneurs peuvent expliquer leur produit ou leur service avec beaucoup de précision.",
        p1b: "Peu sont capables de décrire avec la même clarté la personne qui décide réellement d'acheter.",
        p2a: "Nous avons accès à plus d'information que jamais.",
        p2b: "Mais avoir accès à des données ne veut pas dire comprendre les gens.",
        p3: "Et sans cette compréhension, trop de décisions reposent sur des suppositions.",
      },
      why: {
        title1: "Une entreprise ne peut pas exister sans",
        title_blue: "clients",
        p1: "Une entreprise avance lorsque des gens reconnaissent la valeur de ce qu'elle offre.",
        p2a: "Pas simplement parce que l'idée est bonne.",
        p2b: "Pas seulement parce que le produit est prêt.",
        p2c: "Mais parce qu'une vraie personne reconnaît un besoin et décide de passer à l'action.",
        p3a: "Comprendre cette personne n'est pas secondaire.",
        p3b: "C'est au cœur de tout le reste.",
        imgCaption: "De vraies personnes. De vraies décisions. Votre vraie entreprise.",
      },
      explore: {
        title1: "Un point de",
        title_blue: "départ différent",
        subtitle:
          "Ce livre vous invite à prendre un pas de recul et à revoir votre façon de définir vos clients. Vous y explorerez :",
        items: [
          "pourquoi tant d'entrepreneurs ont de la difficulté à bien définir leurs clients",
          "ce qui manque souvent dans la façon d'aborder la compréhension du client",
          "comment mieux reconnaître les facteurs qui influencent une décision d'achat réelle",
          "comment intégrer cette réflexion de façon plus intentionnelle dans votre entreprise",
        ],
      },
      sample: {
        title1: "Commencez par les",
        title_blue: "3 premiers chapitres",
        body:
          "Si vous n'avez pas encore une idée claire de qui sont réellement vos clients, c'est un bon point de départ. Téléchargez les premiers chapitres et amorcez une réflexion plus concrète sur la manière dont vous définissez et comprenez les personnes que vous cherchez à rejoindre.",
      },
      testimonialsTitle: "Ce que les lecteurs en",
      testimonialsTitleBlue: "disent",
      aboutShort: {
        badge: "L'auteur",
        title: "À propos de l'auteur",
        p1a: "Antoine B. Carrière",
        p1b:
          " est entrepreneur, coach et facilitateur. Depuis plus de deux décennies, il accompagne des personnes qui souhaitent avancer avec plus de clarté dans leurs décisions, leur travail et leurs projets.",
        p2:
          "Son travail met l'accent sur une meilleure compréhension de soi, des autres et des choix à faire pour bâtir une carrière ou une entreprise plus alignée.",
      },
      resourcesPreview: {
        title1: "Allez plus loin avec des",
        title_blue: "ressources pratiques",
        subtitle:
          "Le livre est appuyé par des outils, des articles et du contenu de réflexion conçus pour vous aider à mettre les idées en pratique.",
        cards: {
          tools: {
            title: "Outils",
            body: "Des cadres pratiques pour définir et comprendre vos clients.",
            cta: "Accéder aux outils",
          },
          articles: {
            title: "Articles",
            body: "De courtes réflexions à lire en un seul moment.",
            cta: "Lire les articles",
          },
          quotes: {
            title: "Citations",
            body: "Des phrases tirées du livre à garder avec vous au fil de la semaine.",
            cta: "Voir les citations",
          },
        },
      },
    },
    book: {
      eyebrow: "Le livre",
      title1: "Le",
      title_blue: "livre",
      subtitle:
        "Un point de départ concret pour les entrepreneurs qui veulent mieux comprendre leurs clients.",
      pages: "Pages",
      format: "Format",
      language: "Langue",
      coreIdea: {
        title1: "Une entreprise ne peut pas exister sans",
        title_blue: "clients",
        p1: "Une entreprise avance lorsque des gens reconnaissent la valeur de ce qu'elle offre.",
        p2a: "Pas simplement parce que l'idée est bonne.",
        p2b: "Pas seulement parce que le produit est prêt.",
        p2c: "Mais parce qu'une vraie personne reconnaît un besoin et décide de passer à l'action.",
        p3a: "Comprendre cette personne n'est pas optionnel.",
        p3b: "C'est au cœur de tout le reste.",
      },
      gap: {
        title1: "La majorité des entrepreneurs ne connaissent pas suffisamment leurs",
        title_blue: "clients",
        p1:
          "Les entrepreneurs deviennent souvent très compétents dans ce qu'ils offrent. Mais cette expertise ne s'accompagne pas automatiquement d'une compréhension claire des personnes qu'ils cherchent à servir.",
        p2: "Ce livre s'intéresse précisément à cet écart.",
      },
      gains: {
        title1: "Ce que vous y",
        title_blue: "découvrirez",
        items: [
          "une définition plus claire du client",
          "une meilleure compréhension de ce qui influence une décision d'achat",
          "un meilleur alignement entre votre offre et les personnes que vous voulez rejoindre",
          "une communication plus intentionnelle et plus pertinente",
        ],
      },
      sample: {
        title1: "Lisez avant de vous",
        title_blue: "engager",
        body:
          "Téléchargez les 3 premiers chapitres afin de voir si cette approche rejoint votre manière de réfléchir à votre entreprise et à vos clients.",
      },
      testimonialsTitle: "Lecteurs",
      formats: {
        title: "Formats",
        printAvailable: "Version imprimée disponible",
        otherComing: "Autres formats à venir",
      },
      ctaTitle: "Lisez avant d'acheter.",
      ctaSubtitle:
        "Les 3 premiers chapitres vous diront si ce livre a sa place dans votre bibliothèque.",
    },
    resources: {
      title: "Ressources",
      subtitle:
        "Des outils et du contenu pour vous aider à mieux définir et comprendre vos clients.",
      intro:
        "Tout ce qui se trouve ici est un prolongement du livre. Prenez ce qui vous est utile maintenant et laissez le reste pour plus tard.",
      tools: {
        title: "Outils",
        body: "Des cadres pratiques pour définir et comprendre vos clients. Conçus pour être utilisés, pas seulement lus.",
        cta: "Accéder aux outils",
      },
      articles: {
        title: "Articles",
        body: "De courtes réflexions sur les idées qui sous-tendent le livre — écrites pour être utiles en un seul moment.",
        cta: "Lire les articles",
        comingSoon: "À venir",
      },
      quotes: {
        title: "Citations",
        body: "Des phrases tirées du livre à garder avec vous, à partager avec un collègue ou à méditer seul.",
        cta: "Voir les citations",
      },
      ctaTitle: "La meilleure ressource reste le livre.",
      ctaSubtitle: "Tout ici est conçu pour être utilisé en complément.",
    },
    tools: {
      title: "Outils",
      subtitle: "Des ressources concrètes pour vous aider à mieux définir votre client idéal.",
      note:
        "Chaque outil est conçu pour être utilisé en complément du livre. Des versions téléchargeables complètes sont en préparation.",
      ctaTitle: "Les outils fonctionnent mieux avec le livre.",
      ctaSubtitle: "Commencez par lire les 3 premiers chapitres.",
    },
    about: {
      title: "À propos de l'auteur",
      p1:
        "Antoine B. Carrière est entrepreneur, coach et facilitateur. Depuis plus de deux décennies, il accompagne des personnes qui souhaitent avancer avec plus de clarté dans leurs décisions, leur travail et leurs projets.",
      p2:
        "Son travail met l'accent sur une meilleure compréhension de soi, des autres et des choix à faire pour bâtir une carrière ou une entreprise plus alignée.",
      ctaTitle: "Une question. Un livre entier.",
      ctaSubtitle:
        "Si cela rejoint ce dont vous aviez besoin, commencez par l'extrait.",
    },
    contact: {
      title: "Contact",
      subtitle:
        "Un court message suffit. Écrivez-nous pour toute question ou simplement pour nous faire signe.",
      local: "Local",
      toll: "Sans frais",
      email: "Courriel",
      name: "Votre nom",
      emailLabel: "Courriel",
      message: "Message",
      placeholderName: "Jeanne Tremblay",
      placeholderMsg: "Qu'avez-vous en tête?",
      send: "Envoyer le message",
      sending: "Envoi…",
      success: "Merci pour votre message.",
      successBody: "Votre message a bien été reçu.",
      another: "Écrire à nouveau",
      validation: "Merci de remplir tous les champs.",
      errorToast: "Une erreur est survenue. Réessayez.",
    },
    ctaSection: {
      defaultTitle: "Commencez par les 3 premiers chapitres.",
      defaultSubtitle: "Si ce livre est pour vous, vous le saurez dès la page 30.",
    },
    footer: {
      explore: "Explorer",
      startHere: "Commencez ici",
      startHereBody:
        "La façon la plus simple de savoir si ce livre est pour vous : lire les trois premiers chapitres.",
      copyright: "Tous droits réservés.",
    },
    testimonials: {
      title: "Ce que les lecteurs en",
      titleBlue: "disent",
      readMore: "Voir plus d'avis sur Amazon →",
    },
    login: {
      title: "Connexion administrateur",
      subtitle: "Zone restreinte. Connectez-vous pour gérer le site.",
      passwordLabel: "Mot de passe",
      submit: "Se connecter",
      error: "Mot de passe incorrect.",
      success: "Bienvenue.",
    },
    admin: {
      title: "Tableau de bord",
      welcome: "Gérez vos contacts, messages et contenu.",
      stats: {
        leads: "Téléchargements",
        contacts: "Messages",
      },
      tabs: {
        leads: "Téléchargements",
        contacts: "Messages",
        content: "Contenu",
      },
      exportCsv: "Exporter en CSV",
      noLeads: "Aucun téléchargement pour le moment.",
      noContacts: "Aucun message pour le moment.",
      logout: "Se déconnecter",
      comingSoon: "À venir",
      contentCards: {
        posts: {
          title: "Articles",
          body: "Rédigez et publiez des articles pour soutenir le livre.",
        },
        podcasts: {
          title: "Balados",
          body: "Téléversez des épisodes, ajoutez des notes et branchez votre fil.",
        },
        services: {
          title: "Services",
          body: "Publiez vos ateliers, programmes de développement client et offres de coaching.",
        },
      },
    },
  },
};

export const LANGS = [
  { code: "en", label: "EN" },
  { code: "fr", label: "FR" },
];
